import React, { Dispatch, SetStateAction, ChangeEvent, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import * as Yup from 'yup';
import {
    Button,
    Dialog,
    DialogContentDrawerRenderProps,
    Field as FieldPrimitive,
    Group,
    Icon,
    Select,
    useToast,
} from '@economic/taco';

import { Form, Field } from '../../../../../../../../shared/components/Form';
import track from '../../../../../../../../shared/util/utilities/mixpanel';
import { Journal } from '../../../../../../../../../types/internal-api';
import { BankAccount, SelectableAccount } from '../../../../../../../../../types';
import { t } from '../../../../../../../../shared/components/l10n/l10n';
import { SmartPayCustomerType, smartPayValidAccountTypes } from '../../../../types';

export const DEFAULT_FEE_ACCOUNT_NUMBER = 3628;
interface Props {
    next: () => void;
    back: () => void;
    journals: Journal[];
    journalsState: [number | undefined, Dispatch<SetStateAction<number | undefined>>];
    bankAccounts: BankAccount[];
    bankAccountState: [number | undefined, Dispatch<SetStateAction<number | undefined>>];
    feeAccounts: SelectableAccount[];
    feeAccountState: [number | undefined, Dispatch<SetStateAction<number | undefined>>];
    customerNumberState: [string | undefined, Dispatch<SetStateAction<string | undefined>>];
    customerNumberOptions: string[];
    customerType: SmartPayCustomerType;
    allCustomersOnboarded: boolean;
    onDialogClose: () => void;
    drawerProps?: DialogContentDrawerRenderProps;
}

const PaymentDataStep = ({
    back,
    next,
    journals,
    journalsState: [selectedJournal, setSelectedJournal],
    bankAccounts,
    bankAccountState: [selectedBankAccount, setSelectedBankAccount],
    feeAccounts,
    feeAccountState: [selectedFeeAccount, setSelectedFeeAccount],
    customerNumberState: [selectedCustomerNumber, setSelectedCustomerNumber],
    customerNumberOptions,
    customerType,
    allCustomersOnboarded,
    onDialogClose,
    drawerProps,
}: Props) => {
    const initialValues = {
        customerNumber: selectedCustomerNumber,
    };
    const existingCustomer =
        customerType === SmartPayCustomerType.ExistingCustomerWithIntegration ||
        customerType === SmartPayCustomerType.ExistingCustomerWithoutIntegration;

    const toast = useToast();

    const [isFeeAccountValid, setIsFeeAccountValid] = useState<boolean>(true);
    const [isBankAccountValid, setIsBankAccountValid] = useState<boolean>(true);

    const bankAccountsMap = useMemo<Map<number, SelectableAccount>>(() => {
        const result: Map<number, SelectableAccount> = new Map<number, SelectableAccount>();
        feeAccounts.forEach(f => {
            result.set(f.number, f);
        });

        return result;
    }, [feeAccounts]);

    const isValidBankAccount = accountId => {
        const selectedAccount = accountId && bankAccountsMap.get(accountId);
        return !selectedAccount || smartPayValidAccountTypes.includes(selectedAccount.type.number);
    };

    useEffect(() => {
        setIsFeeAccountValid(isValidBankAccount(selectedFeeAccount));
        setIsBankAccountValid(isValidBankAccount(selectedBankAccount));
    }, [bankAccountsMap]);

    const handleSubmit = () => {
        track('smartpay', 'signup.modal.settings-confirmed');
        if (typeof next === 'function') next();
    };

    const handleCustomerNumberInputChange = (event: ChangeEvent<HTMLInputElement> & { detail: any }) => {
        setSelectedCustomerNumber(event.target.value);
    };

    const setInputValue = (setter: Dispatch<SetStateAction<any>>) => (event: ChangeEvent<HTMLInputElement> & { detail: any }) => {
        setter(event.detail.value);
    };

    const onBankAccountChange = (event: ChangeEvent<HTMLInputElement> & { detail: any }) => {
        const isValid = isValidBankAccount(event.detail.value);
        setIsBankAccountValid(isValid);
        setSelectedBankAccount(event.detail.value);
    };

    const onFeeAccountChange = (event: ChangeEvent<HTMLInputElement> & { detail: any }) => {
        const isValid = isValidBankAccount(event.detail.value);
        setIsFeeAccountValid(isValid);
        setSelectedFeeAccount(event.detail.value);
    };

    const isCustomerNumberValid = () => {
        return selectedCustomerNumber?.match(/^[0-9]{9}$/) !== null;
    };

    const isNextButtonDisbled = () => {
        if (!isFeeAccountValid || !isBankAccountValid) {
            return true;
        }

        if (customerType === SmartPayCustomerType.ExistingCustomerWithIntegration) {
            return !isCustomerNumberValid();
        }

        if (customerType === SmartPayCustomerType.ExistingCustomerWithoutIntegration) {
            return !isCustomerNumberValid() || !selectedJournal || !selectedBankAccount || !selectedFeeAccount;
        }

        return !selectedJournal || !selectedBankAccount || !selectedFeeAccount;
    };

    const getCompleteAccountNumber = (bankRegistrationNumber: string, bankAccountNumber: string) => {
        const initialString = `${bankRegistrationNumber ?? ''}${bankAccountNumber ?? ''}`;

        if ([11, 14].includes(initialString.length)) return initialString;

        const bankAccountNoLength = initialString.length < 11 ? 7 : 10;

        return `${initialString.slice(0, 4).padEnd(4, '0')}${initialString.slice(4).padStart(bankAccountNoLength, '0')}`;
    };

    let paymentDataValidationSchema;
    if (existingCustomer) {
        paymentDataValidationSchema = Yup.object({
            customerNumber: Yup.string()
                .required()
                .matches(/^[0-9]{9}$/, t`SMARTPAY_ONBOARDING_MODAL_COMPANY_DATA_CUSTOMER_NUMBER_VALIDATION_ERROR`),
        });
    }

    if (customerType === SmartPayCustomerType.ExistingCustomerWithIntegration && allCustomersOnboarded) {
        // usually this condition should not trigger
        // used it as a safety net if /checkCustomer endpoint responds late and user manages to
        // go through ConsentStep view.
        onDialogClose();
        toast.error(t`SMARTPAY_ONBOARDING_MODAL_EXISTING_CUSTOMER_CVR_ONBOARDED_ERROR`);
    }

    return (
        <>
            <Dialog.Title>
                <div className="my-4">
                    {existingCustomer
                        ? t`SMARTPAY_ONBOARDING_MODAL_PAYMENT_DATA_EXISTING_CUSTOMER_TITLE`
                        : t`SMARTPAY_ONBOARDING_MODAL_PAYMENT_DATA_TITLE`}
                </div>
            </Dialog.Title>

            <Form
                onSubmit={handleSubmit}
                skipNotifications
                initialData={initialValues}
                validationSchema={paymentDataValidationSchema}>
                <div className="flex flex-col">
                    <span className="my-2">
                        {customerType === SmartPayCustomerType.ExistingCustomerWithoutIntegration
                            ? t`SMARTPAY_ONBOARDING_MODAL_PAYMENT_DATA_DESCRIPTION_TEXT_EXISTING_WITHOUT_INTEGRATION`
                            : customerType === SmartPayCustomerType.ExistingCustomerWithIntegration
                            ? t`SMARTPAY_ONBOARDING_MODAL_PAYMENT_DATA_DESCRIPTION_TEXT_EXISTING_WITH_INTEGRATION`
                            : t`SMARTPAY_ONBOARDING_MODAL_PAYMENT_DATA_DESCRIPTION_TEXT_NEW_CUSTOMER`}
                    </span>

                    {/* customer number */}
                    {customerType === SmartPayCustomerType.ExistingCustomerWithIntegration ? (
                        <FieldPrimitive>
                            {t`SMARTPAY_ONBOARDING_MODAL_PAYMENT_DATA_CUSTOMER_NUMBER_DROPDOWN_TITLE`}
                            <Select
                                data={customerNumberOptions.map(option => ({
                                    text: option,
                                    value: option,
                                }))}
                                onChange={setInputValue(setSelectedCustomerNumber)}
                                value={selectedCustomerNumber}
                            />
                        </FieldPrimitive>
                    ) : customerType === SmartPayCustomerType.ExistingCustomerWithoutIntegration ? (
                        <Field.Text
                            label={t`SMARTPAY_ONBOARDING_MODAL_PAYMENT_DATA_CUSTOMER_NUMBER_DROPDOWN_TITLE`}
                            name="customerNumber"
                            value={selectedCustomerNumber}
                            onChange={handleCustomerNumberInputChange}
                        />
                    ) : null}

                    {/* journal & bank account */}
                    {(customerType === SmartPayCustomerType.ExistingCustomerWithoutIntegration ||
                        customerType === SmartPayCustomerType.NewCustomer) && (
                        <>
                            {/* bank account */}
                            <FieldPrimitive>
                                {t`SMARTPAY_ONBOARDING_MODAL_PAYMENT_DATA_BANK_ACCOUNT_DROPDOWN_TITLE`}
                                <Select
                                    data={bankAccounts.map(account => ({
                                        text: `${account.number} ${account.bankName} - ${getCompleteAccountNumber(
                                            account.bankRegistrationNumber,
                                            account.bankAccountNumber
                                        )}`,
                                        value: account.number,
                                    }))}
                                    onChange={onBankAccountChange}
                                    value={selectedBankAccount}
                                />
                                {!isBankAccountValid && (
                                    <span className="text-red-500">{t`Only accounts of type Profit and loss or Balance can be used in Smart Pay.`}</span>
                                )}
                            </FieldPrimitive>
                            <Link className="self-end px-3 flex items-center" to="/settings/company/bank-accounts">
                                {t`SMARTPAY_ONBOARDING_MODAL_PAYMENT_DATA_BANK_ACCOUNT_SETTINGS_BUTTON`} <Icon name="undock" />
                            </Link>

                            {/* journal */}
                            <FieldPrimitive>
                                {t`SMARTPAY_ONBOARDING_MODAL_PAYMENT_DATA_JOURNAL_DROPDOWN_TITLE`}
                                <Select
                                    data={journals.map(journal => ({ text: journal.name, value: journal.id }))}
                                    onChange={setInputValue(setSelectedJournal)}
                                    value={selectedJournal}
                                />
                            </FieldPrimitive>
                            <Button
                                className="self-end min-h-0"
                                appearance="discrete"
                                onClick={
                                    drawerProps?.toggle
                                }>{t`SMARTPAY_ONBOARDING_MODAL_PAYMENT_DATA_JOURNAL_SETTINGS_BUTTON`}</Button>
                        </>
                    )}

                    {/* fee account */}
                    {(customerType === SmartPayCustomerType.NewCustomer ||
                        customerType === SmartPayCustomerType.ExistingCustomerWithoutIntegration) && (
                        <>
                            <span className="my-2">{t`SMARTPAY_ONBOARDING_MODAL_PAYMENT_DATA_FEE_ACCOUNT_DESCRIPTION_TEXT`}</span>
                            <FieldPrimitive>
                                {t`SMARTPAY_ONBOARDING_MODAL_PAYMENT_DATA_FEE_ACCOUNT_DROPDOWN_TITLE`}
                                <Select
                                    data={feeAccounts.map(account => ({
                                        text: `${account.number} ${account.name}`,
                                        value: account.number,
                                    }))}
                                    onChange={onFeeAccountChange}
                                    value={selectedFeeAccount}
                                    emptyValue={null}
                                    defaultValue={
                                        feeAccounts.length && feeAccounts.some(a => a.number === DEFAULT_FEE_ACCOUNT_NUMBER)
                                            ? DEFAULT_FEE_ACCOUNT_NUMBER
                                            : undefined
                                    }
                                />
                                {!isFeeAccountValid && (
                                    <span className="text-red-500">{t`Only accounts of type Profit and loss or Balance can be used in Smart Pay.`}</span>
                                )}
                            </FieldPrimitive>
                        </>
                    )}
                </div>

                <Dialog.Footer>
                    <Group>
                        <Button onClick={back}>{t`SMARTPAY_ONBOARDING_MODAL_BACK_BUTTON`}</Button>
                        <Button appearance="primary" type="submit" disabled={isNextButtonDisbled()}>
                            {t`SMARTPAY_ONBOARDING_MODAL_NEXT_BUTTON`}
                        </Button>
                    </Group>
                </Dialog.Footer>
            </Form>
        </>
    );
};

export default PaymentDataStep;
